<script>
import ProgressBar from '@/components/atoms/ProgressBar'
import { mapGetters } from 'vuex'

const NEAR_DROPOFF = ['almost_delivering', 'waiting_at_dropoff']
const WINDOW_HEARTBEAT_MS = 60000
const MAX_TIMEOUT_MS = 2147483647

// tasks#202: drives the summary store's clock (summary/tick) so the estimated window's never-past shift moves on the
// open page: a timer set to the displayed window start (rescheduled after every change), a 60 s heartbeat and a tick
// when the page becomes visible again (Summary/Page.vue ticks on Mercure / Pusher reconnects). Stopped while there is
// no window to show (none, delivered, cancelled) and on destroy.
const windowClock = {
    data() {
        return {
            windowTimer: null,
            windowHeartbeat: null,
        }
    },
    computed: {
        clockWindowFrom() {
            const window = this.$store.getters['summary/effectiveEstimatedWindow']
            return window ? window.from.valueOf() : null
        },
    },
    watch: {
        clockWindowFrom: {
            immediate: true,
            handler(from) {
                this.scheduleWindowClock(from)
            },
        },
    },
    mounted() {
        document.addEventListener('visibilitychange', this.onWindowVisibility)
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.onWindowVisibility)
        this.stopWindowClock()
    },
    methods: {
        tickWindowClock() {
            this.$store.dispatch('summary/tick')
        },
        onWindowVisibility() {
            if (!document.hidden && this.clockWindowFrom !== null) this.tickWindowClock()
        },
        scheduleWindowClock(from) {
            clearTimeout(this.windowTimer)
            this.windowTimer = null
            if (from === null) return this.stopWindowClock()
            if (!this.windowHeartbeat)
                this.windowHeartbeat = setInterval(this.tickWindowClock, WINDOW_HEARTBEAT_MS)
            // just after the start passes: the window shifts and the watcher reschedules to the new start; the explicit
            // reschedule covers a timer that fired before the start (unchanged start, no watcher run)
            const delay = Math.min(Math.max(from - Date.now(), 0) + 50, MAX_TIMEOUT_MS)
            this.windowTimer = setTimeout(() => {
                this.windowTimer = null
                this.tickWindowClock()
                this.$nextTick(() => {
                    if (!this.windowTimer) this.scheduleWindowClock(this.clockWindowFrom)
                })
            }, delay)
        },
        stopWindowClock() {
            clearTimeout(this.windowTimer)
            this.windowTimer = null
            if (this.windowHeartbeat) clearInterval(this.windowHeartbeat)
            this.windowHeartbeat = null
        },
    },
}

export default {
    name: 'OrderStatus',
    components: { ProgressBar },
    mixins: [windowClock],
    data() {
        return {
            progress: [0, 0, 0, 0],
            animate: true,
            now: null,
            interval: null,
            watchShowEta: null,
            // hysteresis (tasks#151): the minutes last shown and the ETA behind them
            arrivalMinutes: null,
            arrivalBase: null,
        }
    },
    computed: {
        ...mapGetters({
            ASAP: 'summary/ASAP',
            COURIER_CANCELLED: 'summary/COURIER_CANCELLED',
            COURIER_ERROR: 'summary/COURIER_ERROR',
            PREORDER: 'summary/PREORDER',
            SECTION_0: 'summary/SECTION_0',
            SECTION_1: 'summary/SECTION_1',
            SECTION_2: 'summary/SECTION_2',
            SECTION_3: 'summary/SECTION_3',
            courierStatusText: 'summary/courierStatusText',
            deliveryCancelled: 'summary/deliveryCancelled',
            deliveryFinished: 'summary/deliveryFinished',
            deliveryInitialized: 'summary/deliveryInitialized',
            deliveryJob: 'summary/deliveryJob',
            deliveryStatus: 'summary/deliveryStatus',
            deliveryTime: 'summary/deliveryTime',
            effectiveEstimatedWindow: 'summary/effectiveEstimatedWindow',
            order: 'summary/order',
            orderCancelled: 'summary/orderCancelled',
            orderCancelledAfterApprove: 'summary/orderCancelledAfterApprove',
            orderError: 'summary/orderError',
            restaurantManaged: 'summary/restaurantManaged',
            statusKey: 'summary/statusKey',
            trackOrder: 'summary/trackOrder',
            waitingForUpdate: 'summary/waitingForUpdate',
            showEta: 'summary/showCourier',
            isThirdPartyOrder: 'summary/isThirdPartyOrder',
            unprocessedCanteenOrder: 'summary/unprocessedCanteenOrder',
            orderCancelledByUser: 'summary/orderCancelledByUser',
        }),
        restaurant() {
            return this.order?.restaurant || {}
        },
        courierStatus() {
            return this.deliveryJob?.courierStatus || this.deliveryJob?.courier_status
        },
        cancelledOrder() {
            return this.orderCancelled || this.orderCancelledAfterApprove
        },
        etaToDropoff() {
            return this.deliveryJob?.eta_to_dropoff
        },
        arrivalInputs() {
            return { status: this.deliveryStatus, eta: this.etaToDropoff, now: this.now }
        },
        arrivalLabel() {
            if (NEAR_DROPOFF.includes(this.deliveryStatus))
                return this.$t('order.arrival_any_minute')
            // tasks#202: the estimated window replaces the countdown
            if (this.effectiveEstimatedWindow || !this.etaToDropoff) return this.deliveryTime
            if (this.arrivalMinutes === null) return this.$t('order.arrival_any_minute')
            return this.$t(
                'order.arrival_in_minutes',
                { minutes: this.arrivalMinutes },
                this.arrivalMinutes
            )
        },
        orderLabels() {
            if (!this.trackOrder?.isCanteenSubOrder) return null
            return this.order?.label_codes
                ? JSON.parse(this.order.label_codes)
                : this.order?.items
                ? this.order.items.reduce((p, c) => {
                      if (c.label_code) {
                          if (!p) p = []
                          p = [...p, ...c.label_code]
                      }
                      return p
                  }, null)
                : null
        },
    },
    watch: {
        arrivalInputs: {
            immediate: true,
            handler(next, prev) {
                this.updateArrival(next, prev)
            },
        },
        cancelledOrder: {
            immediate: true,
            handler(cancelledOrder) {
                if (cancelledOrder) this.$store.dispatch('summary/setUpdateStatus', false)
            },
        },
        showEta: {
            immediate: true,
            handler(showEta) {
                if (showEta && !this.restaurantManaged && !this.now) {
                    this.$set(this, 'now', this.$moment().format())
                    this.interval = setInterval(() => {
                        this.$set(this, 'now', this.$moment().format())
                    }, 10000)
                }
            },
        },
        courierStatus: {
            immediate: true,
            handler(courierStatus) {
                if (courierStatus || !this.unprocessedCanteenOrder) {
                    if (
                        [this.COURIER_CANCELLED, this.COURIER_ERROR].includes(courierStatus) &&
                        !this.cancelledOrder
                    ) {
                        this.$store.dispatch('summary/setUpdateStatus', true)
                        this.progress = [0, 0, 0, 0]
                        this.$set(this.progress, 0, (1 / this.SECTION_0.length) * 100)
                    }
                    if (this.deliveryFinished) this.clearTimer()
                    ;[this.SECTION_0, this.SECTION_1, this.SECTION_2, this.SECTION_3].forEach(
                        (states, section) => {
                            const statusIndex = states.findIndex(
                                (status) => status === courierStatus
                            )
                            if (statusIndex > -1) {
                                // this.animate = !(
                                //     section === 1 &&
                                //     statusIndex === 1 &&
                                //     !this.order.asap
                                // )
                                this.$set(
                                    this.progress,
                                    section,
                                    ((statusIndex + 1) / states.length) * 100
                                )
                                for (let i = 0; i < section; i++) {
                                    this.$set(this.progress, i, 100)
                                }
                                for (let i = section + 1; i <= 4; i++) {
                                    this.$set(this.progress, i, 0)
                                }
                            }
                        }
                    )
                } else {
                    this.$set(this.progress, 0, (1 / this.SECTION_0.length) * 100)
                }
            },
        },
    },
    beforeDestroy() {
        this.clearTimer()
    },
    methods: {
        tryAgain() {
            this.$cart.buildCart(this.order)
        },
        clearTimer() {
            if (this.interval) clearInterval(this.interval)
            this.interval = null
        },
        resetArrival() {
            this.arrivalMinutes = null
            this.arrivalBase = null
        },
        updateArrival({ status, eta }, prev) {
            if (prev && prev.status !== status) this.resetArrival()
            if (NEAR_DROPOFF.includes(status) || !eta) return this.resetArrival()
            const etaMoment = this.$moment(eta)
            const seconds = etaMoment.diff(this.$moment(), 'seconds')
            if (seconds <= 0) return this.resetArrival()
            const minutes = Math.ceil(seconds / 60)
            // the shown number may only go up when the ETA itself moved later by at least a minute
            if (
                this.arrivalMinutes === null ||
                minutes <= this.arrivalMinutes ||
                etaMoment.diff(this.arrivalBase, 'seconds') >= 60
            ) {
                this.arrivalMinutes = minutes
                this.arrivalBase = etaMoment.format()
            }
        },
    },
}
</script>

<template lang="pug">
.card
    transition(name="fade" mode="out-in")
        .card-body.delivery-status(v-if="statusKey && !waitingForUpdate && !isThirdPartyOrder")
            h3.text-center {{$t(`enums.${statusKey}.title`)}}
            .font-size-sm.text-center.text-73
                template(v-if="orderError && !orderCancelledByUser") {{orderError}}
                template(v-else) {{$t(`enums.${statusKey}.desc`, {restaurant: restaurant.name, date: $options.filters.toDateTime(order.updated_at)})}}
            button.btn.btn-primary.btn-xs.mx-auto.d-block.mt-4(v-if="orderError && !orderCancelledByUser" @click="tryAgain")
                icon(icon="fa-solid fa-redo").mr-2
                | {{$t('labels.after_payment_error')}}
        .card-body.delivery-status(v-else)
            .d-flex.align-items-center.justify-content-between
                .d-flex.flex-column
                    .text-73.mb-1 {{$t('order.estimated_arrival')}}
                    .text-26.font-weight-semibold.font-size-24.line-1
                        template(v-if="waitingForUpdate") {{$t('enums.delivery_update')}}
                        template(v-else-if="showEta") {{arrivalLabel}}
                        template(v-else) {{deliveryTime}}
                .d-flex.flex-column.justify-content-center.align-items-end(v-if="isThirdPartyOrder && trackOrder && trackOrder.thirdPartyOrderId")
                    .text-73.mb-1 {{ $t("order.third_party.receipt") }}
                    .text-26.font-weight-semibold.font-size-24.line-1 \#{{ trackOrder.thirdPartyOrderId }}
            progress-bar.my-3(:progress="progress" :animate="animate")
            .text-26
                template(v-if="isThirdPartyOrder && trackOrder && trackOrder.thirdPartyTrackingUrl")
                    template {{ $t("order.third_party.tracking.label") }}
                    br
                    a.btn.btn-primary.mt-3(:href="trackOrder.thirdPartyTrackingUrl" target="_blank") {{ $t("order.third_party.tracking.button") }}
                template(v-else-if="statusKey")
                    template {{$t(`enums.${statusKey}.title`)}}&nbsp;
                    template {{$t(`enums.${statusKey}.desc`, {restaurant: restaurant.name})}}
                template(v-else-if="!waitingForUpdate") {{courierStatusText}}
            .d-flex.align-items-center.text-26.border-top.pt-4.mt-6(v-if="orderLabels")
                .d-flex.flex-column
                    span.font-weight-semibold {{ $t("order.label_code.title") }}
                    span {{ $t("order.label_code.desc") }}
                .label-grid.pl-8
                    .px-8.py-3.bg-f0.font-weight-semibold.font-size-h1.border-radius-sm.line-1.text-center.text-nowrap(v-for="orderLabel in orderLabels") {{ orderLabel }}
            .d-flex.align-items-center.text-26.border-top.pt-4.mt-6(v-if="isThirdPartyOrder && trackOrder && trackOrder.thirdPartyCode")
                .d-flex.flex-column
                    span.font-weight-semibold {{ $t("order.third_party.code.title") }}
                    span {{ $t("order.third_party.code.desc") }}
                .ml-8.my-2.px-8.py-3.bg-f0.font-weight-semibold.font-size-h1.border-radius-sm.line-1 {{ trackOrder.thirdPartyCode }}
</template>
<style>
.label-grid {
    width: 100%;
    gap: 10px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(85px, 1fr));
}
</style>
